import type {
  CalendarRecurrence,
  Recurrence,
  RecurrenceInterval,
  RecurrenceStreamType,
  SimpleRecurrence,
} from '@frontend/api-types';
import type { ColorName } from '@frontend/constants';
import type { StrictExclude, StrictExtract } from '@frontend/duck-tape';
import { convertDateLikeToDateISOString, dayjs, pluralize } from '@frontend/duck-tape';
import { match } from 'ts-pattern';
import type { CreateTaskNextSteps } from '../tasks/utils';

export const isSimpleRecurrence = (recurrence: Recurrence | SimpleRecurrence): recurrence is SimpleRecurrence =>
  'state' in recurrence;

export const calendarRecurrenceIntervalString = (calendarRecurrence: CalendarRecurrence) => {
  const intervalString = `Every ${pluralize(calendarRecurrence.interval, calendarRecurrence.intervalCount, true)}`;
  const nextStartDate = dayjs(calendarRecurrence.nextStartDate);

  return match(calendarRecurrence.interval)
    .with('week', () => `${intervalString} on ${nextStartDate.format('ddd')}`)
    .with('month', () => `${intervalString} on the ${nextStartDate.format('Do')}`)
    .with('year', () => `${intervalString} on ${nextStartDate.format('M/D')}`)
    .otherwise(() => intervalString);
};

export const recurrenceIntervalString = (recurrence: Recurrence) => {
  const intervalString = `Every ${pluralize(recurrence.interval, recurrence.intervalCount, true)}`;
  const nextStartDate = dayjs(recurrence.latestTaskDate || recurrence.startDate).add(recurrence.buffer, 'days');

  return match(recurrence.interval)
    .with('week', () => `${intervalString} on ${nextStartDate.format('ddd')}`)
    .with('month', () => `${intervalString} on the ${nextStartDate.format('Do')}`)
    .with('year', () => `${intervalString} on ${nextStartDate.format('M/D')}`)
    .otherwise(() => intervalString);
};

export const recurrenceBufferString = (recurrence: Recurrence) => {
  return pluralize('day', recurrence.buffer, true) + ' before';
};

export const calculateDefaultRecurrenceBuffer = (interval: RecurrenceInterval, intervalCount: number) => {
  const totalDays: number = match(interval)
    .with('day', () => intervalCount * 1)
    .with('week', () => intervalCount * 7)
    .with('month', () => intervalCount * 30)
    .with('year', () => intervalCount * 365)
    .exhaustive();

  const buffer: number = Math.pow(totalDays, 0.65);

  if (buffer < 7) {
    return Math.ceil(buffer);
  }
  return Math.round(buffer / 7) * 7;
};

type RecurrenceStreamTypeData = {
  iconBackgroundColor: ColorName;
  iconColor: ColorName;
  subtitle: string;
  title: string;
};

export const getRecurrenceStreamTypeData = (type: RecurrenceStreamType): RecurrenceStreamTypeData =>
  match<RecurrenceStreamType, RecurrenceStreamTypeData>(type)
    .with('birthday', () => ({
      iconBackgroundColor: 'pink200',
      iconColor: 'pink900',
      subtitle: 'Put your birthdays on autopilot so you never miss another one.',
      title: 'Birthdays',
    }))
    .with('medical', () => ({
      iconBackgroundColor: 'blue200',
      iconColor: 'blue900',
      subtitle: 'Help us put you first (finally!) by putting your health on autopilot.',
      title: 'Medical appointments',
    }))
    .with('self_care', () => ({
      iconBackgroundColor: 'violet200',
      iconColor: 'violet900',
      subtitle: 'Help us put you first (finally!) by putting your wellness on autopilot.',
      title: 'Self care',
    }))
    .with('maintenance', () => ({
      iconBackgroundColor: 'yellow200',
      iconColor: 'yellow900',
      subtitle: 'Because a well-kept home (and car!) means peace of mind on autopilot.',
      title: 'Maintenance',
    }))
    .exhaustive();

// Each suggestion will eventually need to be mapped to an icon name on the specific platform it's being used on
export type BaseRecurrenceSuggestion = {
  title: string;
};

type MedicalRecurrenceSuggestionType = 'clinical_visit' | 'non_clinical_visit';

export type BaseMedicalRecurrenceSuggestion = BaseRecurrenceSuggestion &
  (
    | { specialist: string; type: StrictExtract<MedicalRecurrenceSuggestionType, 'clinical_visit'> }
    | { type: StrictExtract<MedicalRecurrenceSuggestionType, 'non_clinical_visit'> }
  );

export const baseMedicalRecurrenceSuggestions = [
  { title: 'Find prescription supply & renewal', type: 'non_clinical_visit' },
  { specialist: 'optometrist', title: 'Annual eye appointment', type: 'clinical_visit' },
  { title: 'Seasonal flu shot', type: 'non_clinical_visit' },
  { specialist: 'allergist/immunologist', title: 'Allergist/Immunologist', type: 'clinical_visit' },
  { specialist: 'dermatologist', title: 'Dermatologist', type: 'clinical_visit' },
  { specialist: 'cardiologist', title: 'Cardiologist', type: 'clinical_visit' },
  { specialist: 'dentist', title: 'Dentist', type: 'clinical_visit' },
  { specialist: 'endocrinologist', title: 'Endocrinologist', type: 'clinical_visit' },
  { specialist: 'neurologist', title: 'Neurologist', type: 'clinical_visit' },
  { specialist: 'ob/gyn', title: 'Obstetrics/Gynecology (OB/GYN)', type: 'clinical_visit' },
  { specialist: 'pcp', title: 'Primary Care Physician (PCP)', type: 'clinical_visit' },
  { specialist: 'psychiatrist', title: 'Psychiatrist', type: 'clinical_visit' },
  { specialist: 'rheumatologist', title: 'Rheumatologist', type: 'clinical_visit' },
] as const satisfies BaseMedicalRecurrenceSuggestion[];

export type NonClinicalVisitTitle = Extract<
  (typeof baseMedicalRecurrenceSuggestions)[number],
  { type: 'non_clinical_visit' }
>['title'];

export const baseMaintenanceRecurrenceSuggestions = [
  { title: 'Chimney inspection' },
  { title: 'Home repairs' },
  { title: 'Household cleaning' },
  { title: 'Gutter cleaning' },
  { title: 'Landscaping / Gardening / Mowing' },
  { title: 'Oil change' },
  { title: 'Pet grooming' },
  { title: 'Pest control' },
  { title: 'Pool maintenance' },
  { title: 'Snow removal' },
] as const satisfies BaseRecurrenceSuggestion[];

export const baseSelfCareRecurrenceSuggestions = [
  { title: 'Acupuncture' },
  { title: 'Brows' },
  { title: 'Chiropractor' },
  { title: 'Haircut' },
  { title: 'Lashes' },
  { title: 'Makeup & styling' },
  { title: 'Medispa' },
  { title: 'Nails' },
  { title: 'Spas & Massages' },
  { title: 'Waxing and hair removal' },
] as const satisfies BaseRecurrenceSuggestion[];

export const birthdayRecurrenceRadioOptions = [
  { description: '', label: '💌 Remind me', value: 'remind' },
  { description: '', label: '🎁 Send a gift', value: 'send_gift' },
  { description: '', label: '🎉 Plan a party', value: 'plan_party' },
  { description: '', label: '🌸 Send flowers', value: 'flowers' },
] as const;

export type BirthdayRecurrenceRadioOption = (typeof birthdayRecurrenceRadioOptions)[number];

/**
 * Next start date will be: current date + interval length in days next interval
 * If `{selectedInterval: 'year', selectedIntervalCount: 1}` and current date is `1/1/2021`, the next start date will be 2/1/2021
 * If `{selectedInterval: 'week', selectedIntervalCount: 2}` and current date is `1/1/2021`, the next start date will be 1/15/2021
 */
export const calculateNextStartDate = ({
  selectedInterval,
  selectedIntervalCount,
}: {
  selectedInterval: RecurrenceInterval;
  selectedIntervalCount: number;
}) => convertDateLikeToDateISOString(dayjs().add(selectedIntervalCount, selectedInterval));

export const createBirthdayRecurrencePayload = ({
  additionalNotes,
  birthdayDate,
  name,
  recurrenceData,
  selectedBirthdayCelebrationOption,
}: {
  additionalNotes: string;
  birthdayDate: DateISOString;
  name: string;
  recurrenceData: {
    buffer: MaybeUndefined<number>;
    interval: MaybeUndefined<RecurrenceInterval>;
    intervalCount: MaybeUndefined<number>;
    nextStartDate: MaybeUndefined<DateISOString>;
  };
  selectedBirthdayCelebrationOption: MaybeUndefined<BirthdayRecurrenceRadioOption['value']>;
}): {
  buffer: MaybeUndefined<number>;
  description: string;
  interval: MaybeUndefined<RecurrenceInterval>;
  intervalCount: MaybeUndefined<number>;
  name: string;
  nextStartDate: MaybeUndefined<DateISOString>;
  nextSteps: CreateTaskNextSteps;
  recurrenceSuggestionData: {
    birthdayDate: DateISOString;
    entityName: string;
    name: string;
    type: 'birthday';
  };
} => {
  const birthdayTaskName = match(selectedBirthdayCelebrationOption)
    .with('remind', () => `Remind me about ${name}'s birthday`)
    .with('send_gift', () => `Send a gift to ${name}`)
    .with('plan_party', () => `Plan a party for ${name}`)
    .with('flowers', () => `Send flowers to ${name}`)
    .otherwise(() => ``);

  return {
    buffer: recurrenceData.buffer,
    description: `${birthdayTaskName}\n\nAnything else we should know? \n${additionalNotes}`,
    interval: recurrenceData.interval,
    intervalCount: recurrenceData.intervalCount,
    name: birthdayTaskName,
    nextStartDate: recurrenceData.nextStartDate,
    nextSteps: 'Go ahead and complete the task',
    recurrenceSuggestionData: {
      birthdayDate,
      entityName: name,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      name: birthdayRecurrenceRadioOptions.find((option) => option.value === selectedBirthdayCelebrationOption)?.label!,
      type: 'birthday',
    },
  };
};

export const createAppointmentRecurrencePayload = ({
  additionalNotes,
  appointmentReason,
  entityName,
  findOneNearMe,
  recurrenceData,
  suggestionName,
  type,
}: {
  additionalNotes: string;
  appointmentReason: string;
  entityName?: string;
  findOneNearMe?: boolean;
  recurrenceData: {
    buffer: MaybeUndefined<number>;
    interval: MaybeUndefined<RecurrenceInterval>;
    intervalCount: MaybeUndefined<number>;
    nextStartDate: MaybeUndefined<DateISOString>;
  };
  suggestionName: MaybeUndefined<string>;
  type: StrictExclude<RecurrenceStreamType, 'birthday'>;
}): {
  buffer: MaybeUndefined<number>;
  description: string;
  interval: MaybeUndefined<RecurrenceInterval>;
  intervalCount: MaybeUndefined<number>;
  // The recurrence name
  name: string;
  nextStartDate: MaybeUndefined<DateISOString>;
  nextSteps: CreateTaskNextSteps;
  recurrenceSuggestionData: {
    birthdayDate: undefined;
    entityName: MaybeUndefined<string>;
    name: MaybeUndefined<string>;
    type: RecurrenceStreamType;
  };
} => {
  const description = `${appointmentReason} appointment${
    findOneNearMe
      ? ', please help find a provider near me.'
      : ` with ${entityName}, my preferred provider.\n\nAnything else we should know? \n${additionalNotes}`
  }`;

  const recurrenceName = `${appointmentReason} appointment${findOneNearMe ? '' : ` with ${entityName}`}`;

  return {
    buffer: recurrenceData.buffer,
    description,
    interval: recurrenceData.interval,
    intervalCount: recurrenceData.intervalCount,
    name: recurrenceName,
    nextStartDate: recurrenceData.nextStartDate,
    nextSteps: findOneNearMe ? 'Start with some options' : 'Go ahead and complete the task',
    recurrenceSuggestionData: {
      birthdayDate: undefined,
      entityName,
      name: suggestionName,
      type,
    },
  };
};

export const calculateNextStartDateForBirthdayTask = (
  selectedBirthdayDate: DateISOString,
  selectedBirthdayCelebrationOption: BirthdayRecurrenceRadioOption['value'],
) => {
  const currentYear = dayjs().year();
  const adjustedBirthdayDate = selectedBirthdayDate ? dayjs(selectedBirthdayDate).year(currentYear) : undefined;

  const calculatedDate = match(selectedBirthdayCelebrationOption)
    .with('remind', () => dayjs(adjustedBirthdayDate).subtract(7, 'days'))
    .with('send_gift', () => dayjs(adjustedBirthdayDate).subtract(14, 'days'))
    .with('plan_party', () => dayjs(adjustedBirthdayDate).subtract(31, 'days'))
    .with('flowers', () => dayjs(adjustedBirthdayDate).subtract(14, 'days'))
    .exhaustive();

  // If the calculated date is before today, add one year
  const finalDate = calculatedDate.isBefore(dayjs()) ? calculatedDate.add(1, 'year') : calculatedDate;

  return convertDateLikeToDateISOString(finalDate);
};
